import { useEffect, useMemo, useRef, useState } from "react";
import {
  Plus,
  Minus,
  Trash2,
  X,
  Receipt,
  Percent,
  ScrollText,
  PauseCircle,
  PlayCircle,
  Search,
  Barcode,
  Calculator,
  Printer,
  Save,
  Delete,
  MessageSquare,
  CheckSquare,
  User as UserIcon,
  X as XIcon2,
} from "lucide-react";
import { toast } from "sonner";
import { toastFiscalOk, toastFiscalFail } from "@/lib/fiscalToast";
import { http, fmtEUR, warnZeroStock, barcodeQty } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useNavGuard } from "@/lib/NavGuardContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import Numpad from "@/components/Numpad";
import ProductCatalogPanel from "@/components/ProductCatalogPanel";
import ConfirmDialog from "@/components/ConfirmDialog";
import CustomerPickerDialog from "@/components/CustomerPickerDialog";
import CustomerTopItemsDialog from "@/components/CustomerTopItemsDialog";
import { CustomerDialog } from "@/pages/Customers";
import CommentInlinePanel from "@/components/CommentInlinePanel";
import SimpleCommentDialog from "@/components/SimpleCommentDialog";
import ProductNotFoundDialog from "@/components/ProductNotFoundDialog";
import QuickProductDialog from "@/components/QuickProductDialog";
import ManualDepartmentDialog from "@/components/ManualDepartmentDialog";
import { useKeyboard } from "@/lib/KeyboardContext";
// Feb 2026 — Retail kitchen dispatch is now handled server-side via
// POST /pos/kitchen-dispatch, mirroring the Restaurant flow. The old
// client-side `printOrder` import is no longer needed here.
import { printProforma } from "@/lib/proforma";
import { parseScaleBarcode, findProductForScaleCode } from "@/lib/scaleBarcode";
import { confirmDialog, promptDialog } from "@/lib/confirmDialog";

const HOLD_KEY = "ftext_pos_holds_v1";
const COMMENT_PREFIXES = ["ΜΕ", "ΧΩΡΙΣ", "ΛΙΓΟ", "ΠΟΛΥ", "ΜΟΝΟ", "EXTRA"];

function _formatCommentLabel(c, qty, prefix) {
  if (qty === -1) return `ΧΩΡΙΣ ${c.name}`;
  if (qty > 1) return prefix ? `${qty}× ${prefix} ${c.name}` : `${qty}× ${c.name}`;
  return prefix ? `${prefix} ${c.name}` : c.name;
}

// Format quantity: drop trailing zeros (e.g. 2.000 → "2", 1.500 → "1,5")
function formatQty(n) {
  const v = Number(n) || 0;
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(2).replace(/\.?0+$/, "").replace(".", ",");
}

export default function POSRetail() {
  const { open: oskOpenCtx, setOpen: setOskOpenCtx } = useKeyboard();
  const { user } = useAuth();
  const { setDirty } = useNavGuard();
  const [products, setProducts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [settings, setSettings] = useState(null);
  const [printers, setPrinters] = useState([]);
  const [printOverride, setPrintOverride] = useState(""); // selected printer_id to override routing
  const [activeCat, setActiveCat] = useState("all");
  const [cart, setCart] = useState([]);
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [splitPayments, setSplitPayments] = useState([]);
  const [comment, setComment] = useState("");
  const [nonFiscal, setNonFiscal] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  // ✓ Καταχωρήθηκε pulse banner — shown for 3.5 s after each successful
  // sale at the top of the cart panel, matching the «✓ ΠΛΗΡΩΜΕΝΟ» visual
  // language used in Restaurant.jsx. Stores the last receipt counter so
  // the cashier sees the number, not just a generic check.
  const [salePulse, setSalePulse] = useState(null); // null | { receipt: string }
  const [search, setSearch] = useState("");
  const [holds, setHolds] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(HOLD_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const [holdsOpen, setHoldsOpen] = useState(false);
  const [clearConfirmOpen, setClearConfirmOpen] = useState(false);
  // CRM Phase B — attached customer for the current cart.
  // null ⇒ «Λιανική» (anonymous). Reset on every transaction / hold / clear.
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  // Customer card editor — opened when clicking an already-selected chip.
  const [customerEditOpen, setCustomerEditOpen] = useState(false);
  // Phase B+: when user opens «TOP» on a row, we attach the customer
  // immediately and pop a top-items dialog where each item can be added
  // to the cart one-tap (purple [+] buttons).
  const [topItemsCustomer, setTopItemsCustomer] = useState(null);
  // ---- Modifiers / σχόλια (parity with Delivery/Restaurant) ----
  // The rich comment panel replaces the catalog on the right when the
  // operator taps a comment-enabled item, or when adding a product with
  // `auto_open_comment`. State mirrors Restaurant.jsx / DeliveryNewOrder.
  const [comments, setComments] = useState([]);
  const [commentGroups, setCommentGroups] = useState([]);
  const [commentDialog, setCommentDialog] = useState({
    open: false,
    idx: null,
    selections: {},     // commentId → quantity (or -1 for «ΧΩΡΙΣ»)
    prefixes: {},       // commentId → "ΜΕ"/"ΧΩΡΙΣ"/...
    activePrefix: null, // currently-armed prefix
    freeText: "",
  });
  const searchRef = useRef(null);

  // «Αρ. Παραγγελίας» dedicated to the current cart. Reserved atomically
  // from the backend the first time the operator prints a prep ticket
  // (or sends the receipt — whichever happens first) so the paper slip
  // and the fiscal printout always share one number. Cleared whenever
  // the cart is reset (sale, hold, manual clear) so the next sale gets
  // a fresh reservation. If the user prints prep and then voids the
  // cart, the reserved number is left unused — a small gap in the
  // sequence is acceptable per spec.
  const [reservedOrderNo, setReservedOrderNo] = useState(null);

  // Quick-registration / manual-department flows. Triggered when the
  // scanner / typed input cannot be matched against the catalogue.
  // ``notFoundDialog`` holds the scanned-but-unresolved code so the
  // Quick form can pre-fill ``barcode1``. The three states are kept
  // separate (not a finite-state machine) so the operator can move
  // back-and-forth between the picker and the secondary dialogs freely.
  const [notFoundDialog, setNotFoundDialog] = useState({ open: false, barcode: "" });
  const [quickProductOpen, setQuickProductOpen] = useState(false);
  const [manualDeptOpen, setManualDeptOpen] = useState(false);

  // Numpad state — single global instance
  const [numpad, setNumpad] = useState({
    open: false,
    label: "Σκάναρε",
    value: "",
    target: null, // "search" | { kind: "qty"|"price"|"disc", idx }
    allowDot: true,
  });
  const openNumpad = (target, label, initial = "", allowDot = true) =>
    setNumpad({ open: true, target, label, value: String(initial ?? ""), allowDot });
  const closeNumpad = () => setNumpad((n) => ({ ...n, open: false }));
  const setNumpadValue = (v) => setNumpad((n) => ({ ...n, value: v }));

  // Ιούν 2026: ανανέωση αποθέματος μετά από πώληση (badge freshness).
  const refreshProducts = async () => {
    try {
      const r = await http.get("/products?channel=retail");
      setProducts(r.data.filter((x) => x.is_active));
    } catch { /* noop */ }
  };

  const loadAll = async () => {
    const [p, pm, c, s, pr, cm, cg, dp] = await Promise.all([
      http.get("/products?channel=retail"),
      http.get("/payments"),
      http.get("/categories"),
      http.get("/settings"),
      http.get("/printers"),
      http.get("/comments"),
      http.get("/comment-groups"),
      http.get("/departments"),
    ]);
    setProducts(p.data.filter((x) => x.is_active));
    setPayments(pm.data);
    setCategories(c.data);
    setSettings(s.data);
    setPrinters(pr.data || []);
    setComments(cm.data || []);
    setCommentGroups(cg.data || []);
    setDepartments(dp.data || []);
  };

  useEffect(() => {
    const t = setTimeout(loadAll, 0);
    return () => clearTimeout(t);
  }, []);

  // Robust focus helper: retries on the next frame and again ~120 ms later
  // to win the race against Radix Dialog's "restore focus to trigger" logic
  // and any pending React re-renders triggered by reset(). This guarantees
  // that — after every receipt, hold/resume, or numpad close — the cursor
  // lands back on the barcode scanner input so the operator can simply
  // scan the next product.
  const focusSearch = () => {
    const tryFocus = () => {
      const el = searchRef.current;
      if (!el) return false;
      // Only refocus if focus is NOT already on a meaningful interactive
      // element (e.g. another input inside an open dialog). Avoids stealing
      // focus from intentional user actions.
      const active = document.activeElement;
      if (active && active !== el && active.tagName === "INPUT" && active.closest('[role="dialog"]')) {
        return false;
      }
      el.focus();
      // Place cursor at the end if there is any residual text (there
      // shouldn't be after reset, but be defensive).
      try { el.setSelectionRange(el.value.length, el.value.length); } catch { /* non-text input */ }
      return true;
    };
    // Three retry windows: now, next frame, +120 ms (post Radix close).
    setTimeout(tryFocus, 0);
    requestAnimationFrame(() => tryFocus());
    setTimeout(tryFocus, 120);
  };

  useEffect(() => {
    const t = setTimeout(focusSearch, 0);
    return () => clearTimeout(t);
  }, []);

  const persistHolds = (next) => {
    setHolds(next);
    localStorage.setItem(HOLD_KEY, JSON.stringify(next));
  };

  // Navigation guard: block sidebar nav while cart has items.
  useEffect(() => {
    setDirty(
      cart.length > 0,
      "Άδειασε το ταμείο ή βάλε την παραγγελία σε αναμονή πριν φύγεις"
    );
    return () => setDirty(false);
  }, [cart.length, setDirty]);

  const addProduct = (p, addQty = 1) => {
    warnZeroStock(p);
    const existing = cart.findIndex(
      (it) => it.description === p.description && it.price === p.price && !it._edited && (!it.comments || it.comments.length === 0) && !it.free_text
    );
    let affectedIdx;
    if (existing >= 0) {
      const next = [...cart];
      next[existing].qty += addQty;
      setCart(next);
      affectedIdx = existing;
      setSelectedIdx(existing);
    } else {
      affectedIdx = cart.length;
      setCart([
        ...cart,
        {
          product_id: p.id,
          code: p.code || null,
          description: p.description,
          qty: addQty,
          price: p.price,
          vat_rate: p.vat_rate,
          department: p.department,
          no_discount: !!p.no_discount,
          allow_price_change: p.allow_price_change !== false,
          // Modifier-aware fields — propagated from the product so the
          // CartRow knows when to render the «Σχόλιο» button and the
          // comment dialog can resolve the allowed groups.
          allow_comment: !!p.allow_comment,
          comment_group_ids: p.comment_group_ids || [],
          comments: [],
          comment: "",
          free_text: null,
          modifier_total: 0,
        },
      ]);
      setSelectedIdx(cart.length);
    }
    // Auto-open the appropriate editor: rich tile panel when groups
    // are linked, simple free-text dialog otherwise (avoids showing
    // unrelated categories — user-reported bug).
    if (p.allow_comment && p.auto_open_comment) {
      const groupIds = p.comment_group_ids || [];
      setTimeout(() => {
        if (groupIds.length === 0) {
          setSimpleComment({ open: true, idx: affectedIdx });
        } else {
          setCommentDialog({
            open: true,
            idx: affectedIdx,
            selections: {},
            prefixes: {},
            activePrefix: null,
            freeText: "",
          });
        }
      }, 0);
    }
    focusSearch();
  };

  const updateLine = (i, patch) => {
    setCart((prev) =>
      prev.map((it, idx) => (idx === i ? { ...it, ...patch, _edited: true } : it))
    );
  };
  const removeLine = (i) => {
    setCart((prev) => prev.filter((_, idx) => idx !== i));
    if (selectedIdx === i) setSelectedIdx(-1);
  };

  // ---- Comment-panel handlers (Restaurant/Delivery parity) ---------
  const togglePrefix = (p) => {
    setCommentDialog((d) => ({ ...d, activePrefix: d.activePrefix === p ? null : p }));
  };
  const toggleCommentTile = (c) => {
    setCommentDialog((d) => {
      const sel = { ...d.selections };
      const pref = { ...(d.prefixes || {}) };
      if (sel[c.id]) {
        delete sel[c.id];
        delete pref[c.id];
        return { ...d, selections: sel, prefixes: pref };
      }
      if (c.radio_group && !c.radio_multiple) {
        comments
          .filter((x) => x.group_id === c.group_id && x.radio_group === c.radio_group && !x.splittable)
          .forEach((x) => { delete sel[x.id]; delete pref[x.id]; });
      }
      sel[c.id] = 1;
      if (d.activePrefix) pref[c.id] = d.activePrefix;
      return { ...d, selections: sel, prefixes: pref, activePrefix: null };
    });
  };
  const incrementSplit = (c) => {
    setCommentDialog((d) => {
      const sel = { ...d.selections };
      const cur = sel[c.id] || 0;
      if (cur === -1) delete sel[c.id]; // από «ΧΩΡΙΣ» το + πάει στο 0 (χωρίς χρέωση)
      else sel[c.id] = cur + 1;
      return { ...d, selections: sel };
    });
  };
  const decrementSplit = (c) => {
    setCommentDialog((d) => {
      const sel = { ...d.selections };
      const cur = sel[c.id] || 0;
      if (cur === 0) sel[c.id] = -1;
      else if (cur === 1) delete sel[c.id];
      else if (cur === -1) { /* stop */ }
      else sel[c.id] = cur - 1;
      return { ...d, selections: sel };
    });
  };
  const saveComment = () => {
    if (commentDialog.idx == null) return;
    const out = [];
    let modTotal = 0;
    Object.entries(commentDialog.selections).forEach(([cid, qty]) => {
      const c = comments.find((x) => x.id === cid);
      if (!c || qty === 0) return;
      const effectiveQty = Number(qty) || 0;
      const prefix = commentDialog.prefixes?.[cid] || null;
      out.push({
        id: c.id, name: c.name,
        label: _formatCommentLabel(c, effectiveQty, prefix),
        prefix, price_delta: c.price_delta, is_deduction: !!c.is_deduction,
        color: c.color, radio_group: c.radio_group, splittable: c.splittable,
        quantity: effectiveQty,
      });
      const sign = c.is_deduction ? -1 : 1;
      if (effectiveQty > 0) modTotal += sign * Number(c.price_delta || 0) * effectiveQty;
      else if (effectiveQty === -1 && c.is_deduction) modTotal -= Number(c.price_delta || 0);
    });
    const freeText = (commentDialog.freeText || "").trim();
    const labels = out.map((c) => c.label);
    if (freeText) labels.push(freeText);
    updateLine(commentDialog.idx, {
      comments: out,
      comment: labels.join(", "),
      free_text: freeText || null,
      modifier_total: modTotal,
    });
    setCommentDialog({ open: false, idx: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" });
  };
  const [simpleComment, setSimpleComment] = useState({ open: false, idx: null });
  const openCommentDialogForIdx = (idx) => {
    const it = cart[idx];
    if (!it) return;
    const groupIds = it.comment_group_ids || [];
    if (groupIds.length === 0) {
      setSimpleComment({ open: true, idx });
      return;
    }
    const sel = {};
    const pref = {};
    (it.comments || []).forEach((c) => {
      sel[c.id] = c.quantity || 1;
      if (c.prefix) pref[c.id] = c.prefix;
    });
    setCommentDialog({ open: true, idx, selections: sel, prefixes: pref, activePrefix: null, freeText: it.free_text || "" });
  };

  // Virtual `tbl` for CommentInlinePanel — it reads `open_items[idx]`
  // to resolve the item's allowed groups and its description.
  const virtualTbl = useMemo(() => ({ open_items: cart, submitted_batches: [] }), [cart]);

  const totals = useMemo(() => {
    let subtotal = 0;
    let lineDisc = 0;
    for (const it of cart) {
      // Include modifier_total in the line unit price so the cart total
      // matches both the CartRow display and the submit payload (which
      // already adds modifier_total to the price sent to FTEXT).
      const unit = Number(it.price || 0) + Number(it.modifier_total || 0);
      const lineSub = it.qty * unit;
      subtotal += lineSub;
      if (it.discount_percent) lineDisc += (lineSub * it.discount_percent) / 100;
      if (it.discount_amount) lineDisc += it.discount_amount;
    }
    return { subtotal, lineDisc, total: subtotal - lineDisc };
  }, [cart]);

  // Central transient-UI cleanup invoked after a sale, hold/resume cycle,
  // or any other "new receipt" transition. Keeping every reset call in
  // one place protects against future panels/dialogs leaking stale state
  // into the next receipt. Mirrors the same pattern used by
  // ``resetTransientUI`` in Restaurant.jsx for table switches.
  const reset = () => {
    setCart([]);
    setSelectedIdx(-1);
    setSplitPayments([]);
    setComment("");
    setNonFiscal("");
    // Drop the reserved «Αρ. Παραγγελίας» — the next sale gets a fresh one.
    setReservedOrderNo(null);
    // CRM Phase B — every new receipt starts as «Λιανική» (anonymous).
    setSelectedCustomer(null);
    // Transient panels / overlays that should never carry across receipts.
    setPaymentDialogOpen(false);
    setHoldsOpen(false);
    setClearConfirmOpen(false);
    setCustomerPickerOpen(false);
    setNumpad((n) => ({ ...n, open: false }));
    // Modifier dialog must also close — otherwise the right panel stays
    // on the comment editor between receipts.
    setCommentDialog({ open: false, idx: null, selections: {}, prefixes: {}, activePrefix: null, freeText: "" });
    setSearch("");
    // ``lastResult`` is INTENTIONALLY preserved — it's the toast/banner of
    // the just-completed receipt, useful for the cashier to glance at.
    focusSearch();
  };

  // ----- Barcode/search submit -----
  const onSearchEnter = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    tryFindAndAdd(search);
  };

  // ----- Hold / resume -----
  const holdCurrent = () => {
    if (cart.length === 0) {
      toast.error("Καλάθι κενό");
      return;
    }
    const snap = {
      id: `${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
      created_at: new Date().toISOString(),
      cart,
      comment,
      nonFiscal,
      splitPayments,
      total: totals.total,
      // Carry the reserved «Αρ. Παραγγελίας» so the resumed cart prints
      // / sells under the same number — avoids a gap in the global
      // sequence when a prep ticket was printed before holding.
      reservedOrderNo,
      // Carry the attached customer across hold/resume so the operator
      // doesn't re-pick after returning to a parked cart.
      selectedCustomer,
    };
    persistHolds([snap, ...holds]);
    reset();
    toast.success(`Σε αναμονή ${fmtEUR(snap.total)}`);
  };

  const resumeHold = (h) => {
    if (cart.length > 0) {
      // save current first
      const snap = {
        id: `${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
        created_at: new Date().toISOString(),
        cart,
        comment,
        nonFiscal,
        splitPayments,
        total: totals.total,
        reservedOrderNo,
        selectedCustomer,
      };
      persistHolds([snap, ...holds.filter((x) => x.id !== h.id)]);
    } else {
      persistHolds(holds.filter((x) => x.id !== h.id));
    }
    setCart(h.cart || []);
    setComment(h.comment || "");
    setNonFiscal(h.nonFiscal || "");
    setSplitPayments(h.splitPayments || []);
    setReservedOrderNo(h.reservedOrderNo ?? null);
    setSelectedCustomer(h.selectedCustomer || null);
    setSelectedIdx(-1);
    setHoldsOpen(false);
    toast.success("Επαναφορά από αναμονή");
    focusSearch();
  };

  const deleteHold = async (h) => {
    if (!(await confirmDialog("Διαγραφή κράτησης;"))) return;
    persistHolds(holds.filter((x) => x.id !== h.id));
  };

  // ----- Print proforma («Εκτύπωση Παραγγελίας» button) ----------
  // Renders a customer-facing slip with prices, VAT, ΣΥΝΟΛΟ — NOT a
  // kitchen ticket. Does NOT route per printer; one slip with all
  // items. Reserves a global_order_no so the upcoming receipt shares
  // the same paper number.
  const handlePrintOrder = async () => {
    if (cart.length === 0) {
      toast.error("Καλάθι κενό");
      return;
    }
    let orderNo = reservedOrderNo;
    if (orderNo == null) {
      try {
        const r = await http.post("/counters/global-order-no/issue");
        orderNo = r?.data?.order_no ?? null;
        if (orderNo != null) setReservedOrderNo(orderNo);
      } catch { /* non-fatal */ }
    }
    const cust = selectedCustomer;
    const total = cart.reduce(
      (s, it) => s + (Number(it.qty) || 1) * ((Number(it.price) || 0) + (Number(it.modifier_total) || 0)),
      0,
    );
    const proformaItems = cart.map((it) => ({
      description: it.description,
      qty: Number(it.qty || 1),
      price: Number(it.price || 0) + Number(it.modifier_total || 0),
      vat_rate: it.vat_rate,
      // Product identifiers so the backend can fall back to the catalog
      // if ``vat_rate`` is missing on a legacy cart entry.
      product_id: it.product_id || null,
      code: it.code || null,
      // Line-level discount info — the backend renders a
      // «ΕΚΠΤΩΣΗ -X%» row under the item AND a running
      // ΥΠΟΣΥΝΟΛΟ/ΕΚΠΤΩΣΗ block above the total (Feb 2026 fix).
      discount_percent: it.discount_percent ?? null,
      discount_amount: it.discount_amount ?? null,
      comments: it.comments || [],
      free_text: it.free_text || null,
    }));
    // Iter87: route proforma through the Companion thermal printer via
    // the «Προαπόδειξη» template so it works in packaged Kiosk mode
    // (where browser `.print()` calls are silently dropped without the
    // `--kiosk-printing` Chromium flag). Falls back to the browser
    // preview if the backend has no active printer configured.
    try {
      const stationId = localStorage.getItem("selected_station_id") || null;
      const r = await http.post("/print/proforma", {
        items: proformaItems,
        total,
        customer_name: cust?.name || null,
        customer_phone: (cust?.phones && cust.phones[0]) || null,
        order_no: orderNo,
        station_id: stationId,
      });
      if (r?.data?.ok) {
        toast.success(`Προφόρμα στάλθηκε (${r.data.printer || "εκτυπωτής"})`);
        return;
      }
    } catch (e) {
      // Only fall back to browser preview when the backend explicitly
      // says «no printer configured» — real errors surface as a toast.
      const detail = e?.response?.data?.detail || "";
      if (typeof detail === "string" && detail.includes("εκτυπωτής")) {
        toast.info("Χωρίς εκτυπωτή — χρήση browser preview");
      } else {
        toast.error(detail || "Αποτυχία εκτύπωσης προφόρμας");
        return;
      }
    }
    // Fallback: browser preview path (only reached when no active
    // Companion printer exists).
    await printProforma({
      settings,
      globalOrderNo: orderNo,
      customerName: cust?.name || null,
      phone: (cust?.phones && cust.phones[0]) || null,
      items: proformaItems,
      total,
    });
    toast.success("Προφόρμα εκτυπώθηκε");
  };

  // ----- Send items to prep printers («Αποθήκευση»/«Έκδοση Απόδειξης»)
  // Feb 2026 — routed through the SAME backend pipeline as Restaurant so
  // Retail kitchen slips print with the tenant's operator-managed «κουζίνα»
  // template (no prices, no ΣΥΝΟΛΟ, no ΣΑΣ ΕΥΧΑΡΙΣΤΟΥΜΕ — just qty +
  // product + comments + free_text, exactly like Restaurant.jsx).
  //
  // The backend endpoint uses ``emit_kitchen_tickets_for_batch`` with a
  // synthetic table_doc + batch, so printer routing (category.printer_id
  // / product override) and template rendering are identical to the
  // Restaurant module.
  const sendItemsToPrepPrinters = async () => {
    if (cart.length === 0) return;
    try {
      const items = cart.map((it) => ({
        product_id: it.product_id || null,
        description: it.description,
        qty: Number(it.qty || 1),
        comments: it.comments || [],
        free_text: it.free_text || null,
      }));
      const r = await http.post("/pos/kitchen-dispatch", {
        items,
        order_no: reservedOrderNo,
        operator_name: user?.name || user?.username || null,
      });
      const printed = (r.data && r.data.printed) || [];
      if (printed.length) {
        const names = printed.map((p) => p.printer_name).filter(Boolean).join(", ");
        toast.success(`🖨️ Στάλθηκαν στα παρασκευαστήρια: ${names}`);
      }
    } catch {
      /* Kitchen dispatch failure must never break the sale — the
         operator will see the missing slip and can reprint from
         Ιστορικό → Print Jobs. */
    }
  };

  const handleSaveNonFiscal = async () => {
    if (cart.length === 0) {
      toast.error("Καλάθι κενό");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        items: cart.map((c) => ({
          product_id: c.product_id || null,
          description: c.description,
          qty: c.qty,
          price: Math.max(0, Number(c.price || 0) + Number(c.modifier_total || 0)),
          vat_rate: c.vat_rate,
          department: c.department,
          discount_percent: c.discount_percent,
          discount_amount: c.discount_amount,
          comments: c.comments || [],
          free_text: c.free_text || null,
          modifier_total: Number(c.modifier_total || 0),
        })),
      };
      // Attach CRM customer (Phase B) so non-fiscal save also shows up in
      // the customer history view and aggregate totals.
      if (selectedCustomer?.id) payload.customer_id = selectedCustomer.id;
      const r = await http.post("/orders/save-non-fiscal", payload);
      if (r.data.ok) {
        // Fire-and-forget kitchen routing — items without an assigned
        // printer are auto-skipped. Done AFTER server persist so a
        // network failure here doesn't lose the order itself.
        try { await sendItemsToPrepPrinters(); } catch { /* noop */ }
        toast.success("Η παραγγελία αποθηκεύτηκε (χωρίς απόδειξη)");
        refreshProducts();
        reset();
        setPaymentDialogOpen(false);
      } else {
        toast.error("Αποτυχία αποθήκευσης");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Σφάλμα");
    } finally {
      setBusy(false);
    }
  };

  // ----- Submit payment -----
  const submit = async () => {
    if (cart.length === 0) {
      toast.error("Το καλάθι είναι κενό");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        items: cart.map((c) => ({
          product_id: c.product_id || null,
          description: c.description,
          qty: c.qty,
          price: Math.max(0, Number(c.price || 0) + Number(c.modifier_total || 0)),
          vat_rate: c.vat_rate,
          department: c.department,
          discount_percent: c.discount_percent,
          discount_amount: c.discount_amount,
          comments: c.comments || [],
          free_text: c.free_text || null,
          modifier_total: Number(c.modifier_total || 0),
        })),
        payments: splitPayments.length
          ? splitPayments.map((p) => ({
              payment_id: Number(p.payment_id),
              amount: p.amount ? Number(p.amount) : null,
            }))
          : [],
        comment: comment || null,
        non_fiscal_lines: nonFiscal ? nonFiscal.split("\n").filter(Boolean) : [],
        // Reuse the «Αρ. Παραγγελίας» that the prep ticket already
        // reserved (if any). Server stamps the receipt with this exact
        // value so paper slip & fiscal printout share the number; if
        // null, server will atomically issue a fresh one.
        global_order_no: reservedOrderNo,
        // CRM Phase B — attach customer card to the receipt. Server
        // bumps total_spent + orders_count after successful FTEXT send.
        // Per spec, the FTEXT footer does NOT include the customer name
        // on Retail receipts; only Delivery prints «Ευχαριστούμε».
        customer_id: selectedCustomer?.id || null,
      };
      const r = await http.post("/ftext/send-receipt", payload);
      setLastResult(r.data);
      if (r.data.ok) {
        const receiptNo = r.data.log?.receipt_counter || "";
        // Kitchen routing AFTER fiscal print success — items without
        // an assigned printer are auto-skipped (per Feb 2026 spec).
        try { await sendItemsToPrepPrinters(); } catch { /* noop */ }
        toastFiscalOk();
        refreshProducts();
        // Emerald pulse banner at the top of the cart panel for 3.5 s.
        setSalePulse({ receipt: receiptNo });
        setTimeout(() => setSalePulse(null), 3500);
        reset();
        setPaymentDialogOpen(false);
      } else {
        toastFiscalFail();
      }
    } catch {
      toastFiscalFail();
    } finally {
      setBusy(false);
    }
  };

  const onCategoryClick = (catId) => {
    setActiveCat(catId);
    focusSearch();
  };

  // ---------- Numpad accept handler ----------
  const onNumpadAccept = (parsed) => {
    const t = numpad.target;
    if (t === "search") {
      // treat numpad value like the search input value
      setSearch(numpad.value);
      // synthesize an Enter
      tryFindAndAdd(numpad.value);
      // Auto-close the numpad after each scan/manual entry — per user
      // spec the panel must disappear once it's been used so the
      // operator gets the full screen back. The next product is
      // captured by the silent window-level keydown listener (USB
      // scanner) or by tapping the search input again to re-open.
      closeNumpad();
      focusSearch();
      return;
    }
    if (t && typeof t === "object" && t.kind && t.idx != null) {
      const { kind, idx } = t;
      if (parsed == null) {
        closeNumpad();
        return;
      }
      if (kind === "qty") updateLine(idx, { qty: Math.max(0.01, parsed) });
      if (kind === "price") {
        if (cart[idx]?.allow_price_change === false) {
          toast.error("Δεν επιτρέπεται αλλαγή τιμής σε αυτό το είδος");
          closeNumpad();
          return;
        }
        updateLine(idx, { price: Math.max(0, parsed) });
      }
      if (kind === "disc") {
        const it = cart[idx];
        let pct = Math.max(0, parsed);
        if (pct === 0) {
          updateLine(idx, { discount_percent: null });
        } else {
          // Hard cap at 99%
          let capped = Math.min(99, pct);
          // Auto-adjust so net line price >= 0.01
          const gross = (it.qty || 0) * ((it.price || 0) + (Number(it.modifier_total) || 0));
          if (gross > 0) {
            const maxByNet = ((gross - 0.01) / gross) * 100;
            if (capped > maxByNet) capped = Math.max(0, maxByNet);
          }
          // Round to 2 decimals
          capped = Math.round(capped * 100) / 100;
          if (capped < pct) {
            toast.info(`Έκπτωση προσαρμόστηκε σε ${capped}% (όριο 99% και ελάχιστο σύνολο 0,01 €)`);
          }
          updateLine(idx, { discount_percent: capped > 0 ? capped : null });
        }
      }
      closeNumpad();
      return;
    }
    closeNumpad();
  };

  // Reusable search resolver
  const tryFindAndAdd = (q) => {
    const v = (q || "").trim();
    if (!v) return false;
    // 0) Scale EAN-13 — parsed first because the prefix unambiguously
    //    flags this as a weighing-scale ticket, not a plain barcode.
    const scaled = parseScaleBarcode(v, settings);
    if (scaled) {
      const prod = findProductForScaleCode(products, scaled.product_code);
      if (!prod) {
        toast.error(`Ζυγός: δεν βρέθηκε προϊόν με κωδικό ${scaled.product_code}`);
        return false;
      }
      if (prod.sale_paused) {
        toast.error(`Η πώληση του «${prod.description}» είναι σε παύση`);
        return false;
      }
      if (prod.is_weighable) {
        // Both branches build the same cart-line shape — derive once so
        // the price/weight flavours stay locked.
        const unit = Number(prod.price) || 0;
        const qty =
          scaled.mode === "weight"
            ? Number(scaled.value.toFixed(3))
            : unit > 0 ? Number((scaled.value / unit).toFixed(3)) : 1;
        const line = {
          product_id: prod.id,
          description: prod.description,
          qty,
          price: unit,
          vat_rate: prod.vat_rate,
          department: prod.department,
          no_discount: !!prod.no_discount,
          allow_comment: !!prod.allow_comment,
          comment_group_ids: prod.comment_group_ids || [],
          comments: [],
          comment: "",
          free_text: null,
          modifier_total: 0,
          is_weighable: true,
          _edited: true,
        };
        setCart((prev) => [...prev, line]);
        setSelectedIdx(cart.length);
        if (scaled.mode === "price") {
          toast.info(`Ζυγός: αξία ${scaled.value.toFixed(2)} € → ποσότητα ${qty.toFixed(3)}`);
        }
        setSearch("");
        return true;
      }
      // Product is not flagged ζυγιζόμενο — per user spec, ignore the
      // prefix and just add it with qty=1 (the operator probably scanned
      // a similar-looking barcode by accident).
      addProduct(prod);
      setSearch("");
      return true;
    }
    // 1) exact match against any of the 3 barcodes
    let found = products.find(
      (p) =>
        (p.barcode1 || "").trim() === v ||
        (p.barcode2 || "").trim() === v ||
        (p.barcode3 || "").trim() === v
    );
    // 2) exact code match (zero-padded comparison)
    if (!found) {
      const padded = v.padStart(5, "0");
      found = products.find((p) => (p.code || "") === v || (p.code || "") === padded);
    }
    // 3) case-insensitive description includes
    if (!found) {
      const ql = v.toLowerCase();
      found = products.find((p) => p.description.toLowerCase().includes(ql));
    }
    if (found) {
      if (found.sale_paused) {
        toast.error(`Η πώληση του «${found.description}» είναι σε παύση`);
        return false;
      }
      // Πολλαπλασιαστής barcode: σκανάρισμα εξάδας κ.λπ. → qty > 1.
      const mult = barcodeQty(found, v);
      addProduct(found, mult);
      if (mult !== 1) {
        toast.info(`${found.description}: προστέθηκαν ×${mult} (barcode συσκευασίας)`);
      }
      setSearch("");
      return true;
    }
    toast.error(`Δεν βρέθηκε προϊόν: ${v}`);
    // Clear the input so once the dialog closes the operator can scan
    // a fresh barcode without first deleting the failed one.
    setSearch("");
    // Surface the «ΑΜΕΣΗ ΚΑΤΑΧΩΡΗΣΗ / ΧΕΙΡΟΚΙΝΗΤΑ / ΑΚΥΡΩΣΗ» action picker
    // so the operator can immediately recover — register a new item with
    // the scanned barcode, or fall back to a manual department sale.
    setNotFoundDialog({ open: true, barcode: v });
    return false;
  };

  return (
    <div className="h-full flex">
      {/* Cart panel */}
      <section
        className="w-[36%] min-w-[380px] h-full flex flex-col border-r-2 border-slate-300 bg-white"
        data-testid="pos-cart-panel"
      >
        <div className="px-5 py-3.5 border-b-2 border-slate-300 flex items-center justify-between bg-grid relative">
          {salePulse && (
            <div
              className="absolute inset-0 bg-emerald-500 text-white flex items-center justify-center gap-2 animate-pulse z-10 font-heading text-lg font-bold"
              data-testid="sale-success-pulse"
            >
              <CheckSquare size={22} />
              <span>✓ Καταχωρήθηκε</span>
              {salePulse.receipt && (
                <span className="ml-1 text-base opacity-90 font-mono">
                  Απόδειξη #{salePulse.receipt}
                </span>
              )}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h2 className="font-heading text-xl font-bold">Ταμείο</h2>
            <div className="text-xs text-slate-600 mt-0.5 font-bold" data-testid="cart-operator-name">
              {user?.name || "—"}
            </div>
            {/* Customer chip — Phase B. Always visible. Default «Λιανική».
                Click: empty ⇒ open picker; selected ⇒ open the customer
                card in edit mode (with «Αλλαγή Πελάτη» button). */}
            <button
              type="button"
              onClick={() => (selectedCustomer ? setCustomerEditOpen(true) : setCustomerPickerOpen(true))}
              data-testid="pos-customer-btn"
              className={`mt-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border-2 text-xs font-bold transition-all max-w-full ${
                selectedCustomer
                  ? "bg-blue-50 border-blue-400 text-blue-800 hover:bg-blue-100"
                  : "bg-slate-100 border-slate-300 text-slate-600 hover:bg-slate-200 border-dashed"
              }`}
              title={selectedCustomer ? "Καρτέλα πελάτη" : "Επιλογή πελάτη"}
            >
              <UserIcon size={13} className="shrink-0" />
              <span className="truncate" data-testid="pos-customer-label">
                {selectedCustomer ? selectedCustomer.name : "Λιανική"}
              </span>
              {selectedCustomer && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedCustomer(null);
                  }}
                  data-testid="pos-customer-clear"
                  className="ml-0.5 -mr-0.5 hover:bg-blue-200 rounded-full p-0.5"
                >
                  <XIcon2 size={11} />
                </span>
              )}
            </button>
          </div>
          <Button
            onClick={() => {
              if (cart.length === 0) return;
              setClearConfirmOpen(true);
            }}
            data-testid="cart-clear-btn"
            disabled={cart.length === 0}
            className="bg-red-600 hover:bg-red-700 text-white font-bold text-base h-11 px-4 disabled:opacity-40"
          >
            <X size={18} className="mr-1.5" />
            Άδειασμα
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto scroll-thin" data-testid="cart-list">
          {cart.length === 0 && (
            <div className="h-full grid place-items-center text-slate-400 text-sm px-4 text-center">
              {holds.length > 0
                ? `Πάτα «Αναμονή» για να ανακτήσεις παραγγελία (${holds.length} σε αναμονή)`
                : "Σκάναρε προϊόν ή πάτα κουμπί στη λίστα"}
            </div>
          )}
          {cart.map((it, i) => (
            <CartRow
              key={i}
              item={it}
              idx={i}
              selected={i === selectedIdx}
              onSelect={() => setSelectedIdx(i)}
              onChange={(p) => updateLine(i, p)}
              onRemove={() => removeLine(i)}
              openNumpad={openNumpad}
              onCommentClick={openCommentDialogForIdx}
              testid={`cart-row-${i}`}
            />
          ))}
        </div>

        {/* Barcode/search bar ABOVE total — ALWAYS visible */}
        <div className="px-5 py-3 border-t-2 border-slate-300 bg-white">
          <div className="relative">
            <Barcode
              size={20}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <Input
              ref={searchRef}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                if (numpad.open && numpad.target === "search") setNumpadValue(e.target.value);
              }}
              onKeyDown={onSearchEnter}
              onClick={() => openNumpad("search", "", search, false)}
              placeholder="Σκάναρε ή πληκτρολόγησε barcode/κωδικό/περιγραφή..."
              className="pl-10 h-12 text-base font-medium"
              autoFocus
              data-testid="pos-search-input"
            />
            <Search
              size={16}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none"
            />
          </div>
        </div>

        {/* Total + actions (no subtotal) */}
        <div className="border-t-2 border-slate-300 bg-slate-50 px-5 py-4 space-y-2">
          <div className="flex justify-between items-baseline">
            <div className="text-sm font-semibold text-slate-600" data-testid="cart-items-count">
              {(() => {
                const totalQty = cart.reduce((s, it) => s + (Number(it.qty) || 0), 0);
                const lines = cart.length;
                return lines === 0
                  ? "0 τεμ."
                  : `${formatQty(totalQty)} τεμ. · ${lines} γρ.`;
              })()}
            </div>
            <div className="flex items-baseline gap-3">
              <span className="font-heading text-2xl font-bold">Σύνολο</span>
              <span
                className="font-mono tabular text-3xl font-extrabold text-slate-900"
                data-testid="cart-total"
              >
                {fmtEUR(totals.total)}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-5 gap-2 pt-2">
            <Button
              variant="outline"
              className="col-span-2 h-14 border-2 border-amber-400 text-amber-700 hover:bg-amber-50 font-bold text-base relative"
              onClick={() => {
                if (holds.length > 0 && cart.length === 0) setHoldsOpen(true);
                else if (cart.length > 0) holdCurrent();
                else setHoldsOpen(true);
              }}
              data-testid="cart-hold-btn"
            >
              {cart.length === 0 ? (
                <PlayCircle size={18} className="mr-1.5" />
              ) : (
                <PauseCircle size={18} className="mr-1.5" />
              )}
              Αναμονή
              {holds.length > 0 && (
                <Badge
                  className="absolute -top-2 -right-2 bg-amber-500 text-white border-0 font-bold"
                  data-testid="hold-count-badge"
                >
                  {holds.length}
                </Badge>
              )}
            </Button>
            <Button
              variant="outline"
              className="h-14 border-2 border-slate-300 hover:bg-slate-100 font-bold"
              onClick={() =>
                openNumpad("search", "", search, false)
              }
              data-testid="cart-numpad-btn"
              title="Numpad"
            >
              <Calculator size={22} />
            </Button>
            <Button
              className="col-span-2 h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base"
              disabled={cart.length === 0}
              onClick={() => setPaymentDialogOpen(true)}
              data-testid="cart-checkout-btn"
            >
              <Receipt size={18} className="mr-1.5" />
              Πληρωμή
            </Button>
          </div>
        </div>
      </section>

      {/* Right panel — Product grid OR Comment panel when active.
          The `pb-10` reserves a strip clear of the «Made with Emergent»
          badge so the Καταχώρηση button stays clickable. */}
      <div className="flex-1 min-w-0 flex flex-col overflow-hidden pb-10" data-testid="pos-right-panel">
        {commentDialog.open ? (
          <CommentInlinePanel
            tbl={virtualTbl}
            commentDialog={commentDialog}
            setCommentDialog={setCommentDialog}
            comments={comments}
            commentGroups={commentGroups}
            settings={settings}
            COMMENT_PREFIXES={COMMENT_PREFIXES}
            togglePrefix={togglePrefix}
            toggleCommentTile={toggleCommentTile}
            incrementSplit={incrementSplit}
            decrementSplit={decrementSplit}
            saveComment={saveComment}
            oskOpen={oskOpenCtx}
            setOskOpen={setOskOpenCtx}
          />
        ) : (
          <ProductCatalogPanel
            products={products}
            categories={categories}
            activeCat={activeCat}
            setActiveCat={onCategoryClick}
            settings={settings}
            onProductClick={addProduct}
            testidPrefix="pos"
            categoryBorderClass="border-b-2 border-slate-300"
            searchValue={search}
          />
        )}
      </div>

      {/* Payment dialog */}
      <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
        <DialogContent
          hideClose
          className="max-w-3xl p-0 gap-0 overflow-hidden"
          onCloseAutoFocus={(e) => {
            // Prevent Radix from restoring focus to the «Έκδοση Απόδειξης»
            // trigger button — we want the cursor back on the barcode
            // scanner input so the next product can be scanned immediately.
            e.preventDefault();
            focusSearch();
          }}
        >
          <PaymentDialogBody
            total={totals.total}
            payments={payments}
            split={splitPayments}
            setSplit={setSplitPayments}
            comment={comment}
            setComment={setComment}
            nonFiscal={nonFiscal}
            setNonFiscal={setNonFiscal}
            busy={busy}
            settings={settings}
            cartEmpty={cart.length === 0}
            onPrintOrder={handlePrintOrder}
            onSubmit={submit}
            onSaveNonFiscal={handleSaveNonFiscal}
            printers={printers}
            printOverride={printOverride}
            setPrintOverride={setPrintOverride}
          />
        </DialogContent>
      </Dialog>

      {/* Clear-cart confirmation */}
      <ConfirmDialog
        open={clearConfirmOpen}
        onOpenChange={setClearConfirmOpen}
        title="Άδειασμα Ταμείου"
        description="Όλα τα είδη του ταμείου θα διαγραφούν. Η ενέργεια δεν αναιρείται."
        rows={[
          {
            label: "Τμχ προς διαγραφή",
            value: cart.reduce((s, it) => s + Number(it.qty || 0), 0),
          },
          {
            label: "Σύνολο",
            value: fmtEUR(totals.total),
            accent: "red",
          },
        ]}
        confirmLabel="Ναι, άδειασμα"
        cancelLabel="Όχι, πίσω"
        onConfirm={() => {
          reset();
          setClearConfirmOpen(false);
        }}
        busy={busy}
        testidPrefix="cart-clear-confirm"
      />

      {/* Product-not-found action picker — opens automatically when the
          scanner returns nothing. Three follow-ups: register a new item
          on-the-fly, manual department sale, or cancel. */}
      <ProductNotFoundDialog
        open={notFoundDialog.open}
        onOpenChange={(o) => {
          setNotFoundDialog((s) => ({ ...s, open: o }));
          if (!o) focusSearch();
        }}
        barcode={notFoundDialog.barcode}
        onQuickRegister={() => {
          setNotFoundDialog((s) => ({ ...s, open: false }));
          setQuickProductOpen(true);
        }}
        onManual={() => {
          setNotFoundDialog((s) => ({ ...s, open: false }));
          setManualDeptOpen(true);
        }}
      />

      {/* Quick-product registration form. On save the new Product is
          added to the catalogue, pushed onto the cart and the operator
          returns to the scanner input. */}
      <QuickProductDialog
        open={quickProductOpen}
        onOpenChange={(o) => {
          setQuickProductOpen(o);
          if (!o) focusSearch();
        }}
        initialBarcode={notFoundDialog.barcode}
        departments={departments}
        categories={categories}
        onCreated={(prod) => {
          // Mirror the regular catalogue: keep the in-memory list in
          // sync so the next scan of the same barcode finds the item
          // without a page reload.
          setProducts((prev) => [...prev, prod].filter((x) => x.is_active));
          addProduct(prod);
        }}
      />

      {/* Manual department sale — bypasses Product creation entirely.
          Adds an ad-hoc cart line carrying the department's VAT class. */}
      <ManualDepartmentDialog
        open={manualDeptOpen}
        onOpenChange={(o) => {
          setManualDeptOpen(o);
          if (!o) focusSearch();
        }}
        departments={departments}
        onSubmit={(line) => {
          // Mirror ``addProduct`` shape so downstream code (totals,
          // FTEXT mapping) stays uniform.
          setCart((prev) => [
            ...prev,
            {
              description: line.description,
              qty: 1,
              price: line.price,
              vat_rate: line.vat_rate,
              department: line.department,
              no_discount: false,
              allow_comment: false,
              comment_group_ids: [],
              comments: [],
              comment: "",
              free_text: null,
              modifier_total: 0,
              _edited: true,
            },
          ]);
          setSelectedIdx(cart.length);
          toast.success(`Προστέθηκε: ${line.description} ${fmtEUR(line.price)}`);
        }}
      />

      {/* Customer card editor — opens on chip click when a customer is
          already selected. «Αλλαγή Πελάτη» flips to the picker. */}
      <SimpleCommentDialog
        open={simpleComment.open}
        onOpenChange={(o) => setSimpleComment({ open: o, idx: o ? simpleComment.idx : null })}
        item={simpleComment.idx != null ? cart[simpleComment.idx] : null}
        initialText={simpleComment.idx != null ? (cart[simpleComment.idx]?.free_text || cart[simpleComment.idx]?.comment || "") : ""}
        onSave={(text) => {
          if (simpleComment.idx == null) return;
          const trimmed = (text || "").trim();
          updateLine(simpleComment.idx, {
            free_text: trimmed || null,
            comment: trimmed,
            comments: [],
          });
        }}
      />

      <CustomerDialog
        open={customerEditOpen}
        onOpenChange={setCustomerEditOpen}
        customer={selectedCustomer}
        onChangeCustomer={() => { setCustomerEditOpen(false); setCustomerPickerOpen(true); }}
        onSaved={async () => {
          if (selectedCustomer?.id) {
            try {
              const r = await http.get(`/customers/${selectedCustomer.id}`);
              setSelectedCustomer(r.data);
            } catch (e) { /* noop */ }
          }
        }}
      />

      {/* Customer picker — Phase B CRM */}
      <CustomerPickerDialog
        open={customerPickerOpen}
        onOpenChange={setCustomerPickerOpen}
        onSelect={(c) => {
          setSelectedCustomer(c);
          toast.success(`Επιλέχθηκε: ${c.name}`);
        }}
        onSelectWithRepeat={({ customer, lastOrder }) => {
          setSelectedCustomer(customer);
          // Replace the current cart with the items from the last
          // successful receipt. We trust the persisted shape — fields
          // already match CartItem.
          const items = (lastOrder.items || []).map((it) => ({
            description: it.description,
            qty: Number(it.qty || 1),
            price: Number(it.price || 0),
            vat_rate: it.vat_rate,
            department: it.department,
            discount_percent: it.discount_percent ?? null,
            discount_amount: it.discount_amount ?? null,
            comments: it.comments || [],
            free_text: it.free_text || null,
            modifier_total: Number(it.modifier_total || 0),
          }));
          setCart(items);
          setSelectedIdx(items.length - 1);
          setSplitPayments([]);
          setReservedOrderNo(null);
          toast.success(
            `Επανάληψη τελευταίας: ${customer.name} · ${items.length} είδ. · ${Number(lastOrder.total_amount || 0).toFixed(2)}€`,
          );
        }}
        onSelectWithTopItems={(c) => {
          setSelectedCustomer(c);
          setTopItemsCustomer(c);
        }}
      />

      {/* Top-items dialog (Phase B+ — «Συχνότερα Προϊόντα Πελάτη») */}
      {topItemsCustomer && (
        <CustomerTopItemsDialog
          customer={topItemsCustomer}
          onOpenChange={(o) => !o && setTopItemsCustomer(null)}
          onAddItem={(it) => addProduct(it)}
        />
      )}

      {/* Holds dialog */}
      <Dialog open={holdsOpen} onOpenChange={setHoldsOpen}>
        <DialogContent
          hideClose
          className="max-w-2xl border-2 border-slate-300 shadow-2xl p-0 gap-0 overflow-hidden"
          data-testid="holds-dialog"
        >
          <DialogHeader className="px-6 py-4 bg-amber-50 border-b-2 border-amber-300">
            <DialogTitle className="flex items-center gap-3 text-2xl font-heading font-bold text-amber-800">
              <PauseCircle size={28} />
              Παραγγελίες σε Αναμονή ({holds.length})
            </DialogTitle>
            <div className="text-sm text-amber-700 mt-1">
              Πάτα μια παραγγελία για επαναφορά στο ταμείο
            </div>
          </DialogHeader>
          <div className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[65vh] overflow-y-auto scroll-thin">
            {holds.length === 0 && (
              <div className="col-span-full text-center py-12">
                <PauseCircle size={48} className="mx-auto text-slate-300 mb-2" />
                <div className="text-slate-400 text-base font-medium">
                  Δεν υπάρχουν παραγγελίες σε αναμονή.
                </div>
              </div>
            )}
            {holds.map((h) => (
              <div
                key={h.id}
                className="border-2 border-slate-300 rounded-xl bg-white shadow-sm hover:shadow-md hover:border-emerald-500 active:scale-[0.98] cursor-pointer transition-all overflow-hidden"
                data-testid={`hold-row-${h.id}`}
                onClick={() => resumeHold(h)}
              >
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex justify-between items-center">
                  <div className="font-mono text-xs text-slate-600 font-bold">
                    {new Date(h.created_at).toLocaleTimeString("el-GR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                  <div className="text-xs text-slate-500 font-bold">
                    {h.cart.length} είδ{h.cart.length === 1 ? "ος" : "η"}
                  </div>
                </div>
                <div className="px-4 py-4 flex items-baseline justify-between">
                  <span className="font-heading font-bold text-slate-700">Σύνολο</span>
                  <span className="font-mono tabular text-3xl font-extrabold text-emerald-700">
                    {fmtEUR(h.total)}
                  </span>
                </div>
                <div className="px-4 pb-3 text-xs text-slate-500 line-clamp-2 min-h-[32px]">
                  {h.cart.map((it) => `${it.qty}× ${it.description}`).join(" • ")}
                </div>
                <div className="grid grid-cols-3 gap-0 border-t-2 border-slate-200">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      resumeHold(h);
                    }}
                    className="col-span-2 h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-base flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
                    data-testid={`hold-resume-${h.id}`}
                  >
                    <PlayCircle size={20} />
                    Επαναφορά
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteHold(h);
                    }}
                    className="h-14 bg-red-50 hover:bg-red-100 text-red-700 font-bold border-l-2 border-slate-200 flex items-center justify-center active:scale-[0.97] transition-all"
                    data-testid={`hold-del-${h.id}`}
                  >
                    <Trash2 size={20} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="px-4 py-3 border-t-2 border-slate-200 bg-slate-50 flex justify-end">
            <Button
              onClick={() => setHoldsOpen(false)}
              variant="outline"
              className="h-11 px-6 border-2 border-slate-400 text-slate-800 hover:bg-white font-bold"
              data-testid="holds-back-btn"
            >
              <X size={16} className="mr-1.5" />
              Επιστροφή
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* «Αποτέλεσμα Driver» — φαίνεται μόνο σε ΑΠΟΤΥΧΙΑ. Στην επιτυχή
          έκδοση απόδειξης έχουμε ήδη το emerald pulse banner + toast
          + QR link, οπότε το modal θα ήταν περιττή διακοπή. */}
      {lastResult && !lastResult.ok && (
        <Dialog open={!!lastResult && !lastResult.ok} onOpenChange={() => setLastResult(null)}>
          <DialogContent className="max-w-2xl" data-testid="last-result-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ScrollText size={16} /> Αποτέλεσμα Driver
              </DialogTitle>
            </DialogHeader>
            <ResultViewer log={lastResult.log} />
          </DialogContent>
        </Dialog>
      )}

      {/* Global floating numpad */}
      <Numpad
        open={numpad.open}
        label={numpad.label}
        value={numpad.value}
        allowDot={numpad.allowDot}
        onChange={(v) => {
          setNumpadValue(v);
          if (numpad.target === "search") setSearch(v);
        }}
        onAccept={onNumpadAccept}
        onClose={closeNumpad}
      />
    </div>
  );
}

export function CartRow({ item, idx, selected, onSelect, onChange, onRemove, openNumpad, testid, onCommentClick }) {
  const unit = Number(item.price || 0) + Number(item.modifier_total || 0);
  const gross = item.qty * unit;
  const disc = item.discount_percent
    ? (gross * item.discount_percent) / 100
    : item.discount_amount || 0;
  const net = Math.max(0, gross - disc);
  const hasDiscount = disc > 0;
  return (
    <div
      onClick={onSelect}
      data-testid={testid}
      className={`px-4 py-3 border-b border-slate-100 cursor-pointer transition-colors ${
        selected
          ? "bg-blue-50"
          : (idx % 2 === 0 ? "bg-white" : "bg-slate-50") + " hover:bg-blue-50/60"
      }`}
    >
      <div className="flex justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-lg truncate leading-tight">{item.description}</div>
          <div className="text-sm text-slate-600 font-mono tabular mt-0.5">
            {fmtEUR(item.price)} × {item.qty}
            {item.modifier_total > 0 ? `  +${fmtEUR(item.modifier_total)} extra` : ""}
            {item.modifier_total < 0 ? `  −${fmtEUR(Math.abs(item.modifier_total))}` : ""}
            {item.discount_percent ? `  •  -${item.discount_percent}%` : ""}
          </div>
          {item.comment && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (onCommentClick) onCommentClick(idx);
              }}
              data-testid={`${testid}-comment`}
              className="mt-1 text-sm font-medium text-blue-700 italic hover:underline text-left leading-snug"
            >
              › {item.comment}
            </button>
          )}
        </div>
        <div className="text-right shrink-0">
          {hasDiscount ? (
            <>
              <div className="font-mono tabular text-sm text-slate-400 line-through leading-none">
                {fmtEUR(gross)}
              </div>
              <div className="font-mono tabular font-bold text-lg text-emerald-700 leading-tight">
                {fmtEUR(net)}
              </div>
            </>
          ) : (
            <div className="font-mono tabular font-bold text-lg">{fmtEUR(net)}</div>
          )}
        </div>
      </div>
      {selected && (
        <div
          className={`mt-3 grid gap-2 ${item.allow_comment && onCommentClick ? "grid-cols-8" : "grid-cols-7"}`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Quantity */}
          <button
            onClick={() => openNumpad({ kind: "qty", idx }, "ΠΟΣΟΤΗΤΑ", "")}
            className="col-span-2 h-14 rounded-lg border-2 border-slate-300 bg-white hover:bg-blue-50 active:scale-[0.97] transition-all flex flex-col items-center justify-center"
            data-testid={`${testid}-qty`}
          >
            <div className="text-[10px] tracking-wider text-slate-500 font-bold">
              ΠΟΣΟΤΗΤΑ
            </div>
            <div className="text-2xl font-bold font-mono tabular leading-none">
              {item.qty}
            </div>
          </button>
          {/* Price */}
          <button
            onClick={() => openNumpad({ kind: "price", idx }, "ΤΙΜΗ €", "")}
            className="col-span-2 h-14 rounded-lg border-2 border-slate-300 bg-white hover:bg-blue-50 active:scale-[0.97] transition-all flex flex-col items-center justify-center"
            data-testid={`${testid}-price`}
          >
            <div className="text-[10px] tracking-wider text-slate-500 font-bold">
              ΤΙΜΗ €
            </div>
            <div className="text-2xl font-bold font-mono tabular leading-none">
              {Number(item.price).toFixed(2)}
            </div>
          </button>
          {/* Discount */}
          <button
            onClick={() => {
              if (item.no_discount) {
                toast.error("Δεν επιτρέπεται έκπτωση σε αυτό το είδος");
                return;
              }
              openNumpad({ kind: "disc", idx }, "ΕΚΠΤΩΣΗ %", "");
            }}
            disabled={!!item.no_discount}
            className={`col-span-2 h-14 rounded-lg border-2 transition-all flex flex-col items-center justify-center ${
              item.no_discount
                ? "border-slate-200 bg-slate-100 opacity-60 cursor-not-allowed"
                : "border-slate-300 bg-white hover:bg-blue-50 active:scale-[0.97]"
            }`}
            data-testid={`${testid}-disc`}
          >
            <div className="text-[10px] tracking-wider text-slate-500 font-bold">
              {item.no_discount ? "ΟΧΙ ΕΚΠΤΩΣΗ" : "ΕΚΠΤΩΣΗ %"}
            </div>
            <div className="text-2xl font-bold font-mono tabular leading-none text-red-600">
              {item.discount_percent ? `-${item.discount_percent}` : "—"}
            </div>
          </button>
          {/* Remove */}
          <button
            onClick={onRemove}
            className={`${item.allow_comment && onCommentClick ? "col-span-1" : "col-span-1"} h-14 rounded-lg border-2 border-red-300 bg-red-50 text-red-600 hover:bg-red-100 active:scale-[0.97] transition-all flex items-center justify-center`}
            data-testid={`${testid}-remove`}
          >
            <Trash2 size={22} />
          </button>
          {/* Comment (only when allowed) — rightmost */}
          {item.allow_comment && onCommentClick && (
            <button
              onClick={() => onCommentClick(idx)}
              data-testid={`${testid}-comment-btn`}
              className={`col-span-1 h-14 rounded-lg border-2 transition-all flex items-center justify-center ${
                item.comment
                  ? "border-blue-500 bg-blue-50 text-blue-700"
                  : "border-blue-300 bg-white text-blue-600 hover:bg-blue-50 active:scale-[0.97]"
              }`}
              title="Σχόλιο"
            >
              <MessageSquare size={22} />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function PaymentSection({ total, payments, split, setSplit }) {
  const addSplit = (pid) => setSplit([...split, { payment_id: pid, amount: "" }]);
  const sumSplit = split.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  return (
    <div className="space-y-3">
      <div className="text-xs text-slate-500">
        Διάλεξε μέθοδο για πληρωμή ολόκληρου του ποσού, ή πρόσθεσε πολλαπλές.
      </div>
      <div className="grid grid-cols-2 gap-2" data-testid="payment-methods">
        {payments.map((p) => (
          <button
            key={p.id}
            onClick={() => addSplit(p.payment_id)}
            className="border border-slate-200 rounded-md py-3 px-3 text-sm font-medium hover:bg-blue-50 active:scale-[0.97] transition-all"
            data-testid={`pay-method-${p.payment_id}`}
          >
            <div className="font-semibold">{p.name}</div>
            <div className="text-xs text-slate-500">#{p.payment_id}</div>
          </button>
        ))}
      </div>
      {split.length > 0 && (
        <div className="space-y-2 border-t pt-3">
          <div className="text-xs font-semibold text-slate-700">Πληρωμές</div>
          {split.map((p, i) => {
            const pm = payments.find((x) => x.payment_id === Number(p.payment_id));
            return (
              <div key={i} className="flex items-center gap-2" data-testid={`split-row-${i}`}>
                <div className="flex-1 text-sm">{pm?.name || p.payment_id}</div>
                <Input
                  type="number"
                  step="0.01"
                  value={p.amount}
                  placeholder="Υπόλοιπο"
                  onChange={(e) => {
                    const next = [...split];
                    next[i].amount = e.target.value;
                    setSplit(next);
                  }}
                  className="h-9 w-28 text-right font-mono"
                  data-testid={`split-amount-${i}`}
                />
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => setSplit(split.filter((_, idx) => idx !== i))}
                  data-testid={`split-remove-${i}`}
                >
                  <Trash2 size={14} />
                </Button>
              </div>
            );
          })}
          <div className="text-xs text-slate-500 text-right">
            Άθροισμα: {fmtEUR(sumSplit)} / {fmtEUR(total)}
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------- New Payment Dialog -----------------------------

// Exported so other modules (Restaurant close, Delivery payment) can reuse
// the same payment UI for consistency.
export function PaymentDialogBody({
  total,
  payments,
  split,
  setSplit,
  comment,
  setComment,
  nonFiscal,
  setNonFiscal,
  busy,
  settings,
  cartEmpty,
  onPrintOrder,
  onSubmit,
  onSaveNonFiscal,
  printers = [],
  printOverride = "",
  setPrintOverride = () => {},
}) {
  // Identify cash payment by name (preferred) or fallback to payment_id=1
  const cash = useMemo(
    () =>
      payments.find((p) => /ΜΕΤΡΗΤ/i.test(p.name || "")) ||
      payments.find((p) => p.payment_id === 1) ||
      payments[0],
    [payments]
  );
  const [activePid, setActivePid] = useState(null);
  const [received, setReceived] = useState("");
  const [showExtras, setShowExtras] = useState(false);

  // Initial setup: prefer Cash, otherwise first payment method available
  useEffect(() => {
    if (activePid === null && cash) {
      const t = setTimeout(() => setActivePid(cash.payment_id), 0);
      return () => clearTimeout(t);
    }
  }, [cash, activePid]);

  // Push the current selection into splitPayments so backend gets it
  useEffect(() => {
    const t = setTimeout(() => {
      if (!activePid) {
        setSplit([]);
        return;
      }
      const amt = parseFloat(String(received).replace(",", "."));
      const isCash = cash && activePid === cash.payment_id;
      setSplit([
        {
          payment_id: activePid,
          amount: isCash && Number.isFinite(amt) ? String(amt) : "",
        },
      ]);
    }, 0);
    return () => clearTimeout(t);
  }, [activePid, received, cash, setSplit]);

  const numericReceived = parseFloat(String(received).replace(",", ".")) || 0;
  const isCashActive = cash && activePid === cash.payment_id;
  const change = isCashActive && numericReceived > 0 ? numericReceived - total : 0;
  // Guard: όταν ο ταμίας πληκτρολογήσει «Ληφθέντα Μετρητά», δεν επιτρέπεται
  // να είναι μικρότερα από το σύνολο (10€ σύνολο / 5€ δοσμένα → block).
  // Επιτρέπεται όμως να το αφήσει κενό (implicit «ακριβώς το ποσό»).
  const cashInsufficient = isCashActive && numericReceived > 0 && numericReceived + 0.005 < total;

  const press = (k) => {
    setReceived((v) => {
      if (k === "back") return v.slice(0, -1);
      if (k === "clear") return "";
      if (k === ",") return v.includes(".") ? v : v.length ? v + "." : "0.";
      return v + k;
    });
  };
  // Quick-add buttons (5€, 10€, 20€, 50€)
  const addQuick = (amount) => {
    const current = parseFloat(String(received).replace(",", ".")) || 0;
    const next = current + amount;
    setReceived(next.toFixed(2));
  };

  const keys = ["7", "8", "9", "4", "5", "6", "1", "2", "3", ",", "0", "back"];

  return (
    <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] divide-x divide-slate-200" data-testid="payment-dialog-body">
      {/* LEFT: total, received, change, methods, action row */}
      <div className="p-5 space-y-4">
        {/* Big total */}
        <div className="rounded-xl bg-slate-900 text-white px-5 py-4 flex items-baseline justify-between">
          <span className="text-sm uppercase tracking-wider text-slate-300">Σύνολο</span>
          <span className="font-mono tabular text-4xl md:text-5xl font-extrabold text-emerald-400" data-testid="payment-total">
            {fmtEUR(total)}
          </span>
        </div>

        {/* Received cash + change */}
        <div className={`rounded-xl border-2 p-4 ${isCashActive ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-slate-50/60"}`}>
          <Label className="text-xs uppercase tracking-wider font-bold">
            {isCashActive ? "Ληφθέντα Μετρητά" : "Ποσό Πληρωμής"}
          </Label>
          <Input
            value={received}
            onChange={(e) => setReceived(e.target.value.replace(",", "."))}
            placeholder={fmtEUR(total)}
            className="h-14 text-3xl font-mono tabular text-right font-bold border-2 mt-1"
            data-testid="payment-received-input"
            autoFocus
          />
          {isCashActive && (
            <div className="flex items-baseline justify-between mt-3 px-1">
              <span className="text-sm uppercase font-bold tracking-wider text-slate-700">Ρέστα</span>
              <span
                className={`font-mono tabular text-3xl font-extrabold ${
                  change >= 0 ? "text-emerald-700" : "text-red-600"
                }`}
                data-testid="payment-change"
              >
                {fmtEUR(change >= 0 ? change : -change)}
                {change < 0 && <span className="text-sm font-normal ml-2">(λείπει)</span>}
              </span>
            </div>
          )}
          {isCashActive && (
            <div className="grid grid-cols-5 gap-2 mt-3">
              <button
                type="button"
                onClick={() => setReceived(total.toFixed(2))}
                className="h-10 rounded-md border-2 border-blue-300 bg-blue-50 text-blue-800 font-bold text-sm hover:bg-blue-100 active:scale-[0.97]"
                data-testid="quick-exact"
              >
                Ακριβώς
              </button>
              {[5, 10, 20, 50].map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => addQuick(v)}
                  className="h-10 rounded-md border-2 border-slate-300 bg-white font-bold font-mono text-sm hover:bg-slate-50 active:scale-[0.97]"
                  data-testid={`quick-${v}`}
                >
                  +{v}€
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Payment methods */}
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold mb-1.5">
            Τρόπος Πληρωμής
          </div>
          <div className="grid grid-cols-2 gap-2" data-testid="payment-methods">
            {payments.map((p) => {
              const active = activePid === p.payment_id;
              const isCashOpt = cash && p.payment_id === cash.payment_id;
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => setActivePid(p.payment_id)}
                  data-testid={`pay-method-${p.payment_id}`}
                  className={`rounded-lg border-2 py-3 px-3 text-sm font-bold transition-all active:scale-[0.97] ${
                    active
                      ? "bg-emerald-600 text-white border-emerald-700 shadow-md"
                      : "bg-white border-slate-300 text-slate-800 hover:border-blue-400 hover:bg-blue-50"
                  }`}
                >
                  <div className="text-base">{p.name}</div>
                  {isCashOpt && (
                    <div className={`text-[10px] uppercase mt-0.5 tracking-wider ${active ? "text-emerald-100" : "text-slate-500"}`}>
                      Προεπιλογή
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Optional extras (comment / non-fiscal) */}
        <div>
          <button
            type="button"
            onClick={() => setShowExtras((s) => !s)}
            className="text-xs text-slate-500 hover:text-slate-800 underline-offset-2 hover:underline"
            data-testid="payment-toggle-extras"
          >
            {showExtras ? "▾ Απόκρυψη σχολίου / μη φορολογικών" : "▸ Σχόλιο ή μη φορολογικές γραμμές"}
          </button>
          {showExtras && (
            <div className="space-y-2 pt-2">
              <Input
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Σχόλιο απόδειξης..."
                className="h-9"
                data-testid="receipt-comment"
              />
              <textarea
                className="w-full min-h-[60px] rounded-md border border-slate-200 px-3 py-2 text-sm"
                placeholder="Μη φορολογικές γραμμές (μία ανά γραμμή)..."
                value={nonFiscal}
                onChange={(e) => setNonFiscal(e.target.value)}
                data-testid="receipt-nonfiscal"
              />
            </div>
          )}
        </div>

        {/* Print order + Diskette side-by-side */}
        <div className="space-y-2">
          {printers.filter((p) => p.active).length > 1 && (
            <select
              value={printOverride}
              onChange={(e) => setPrintOverride(e.target.value)}
              className="w-full h-9 px-2 border border-slate-300 rounded-md bg-white text-xs font-bold"
              data-testid="print-override-select"
              title="Override εκτυπωτή για όλα τα είδη (αλλιώς routing ανά κατηγορία)"
            >
              <option value="">Auto (routing ανά κατηγορία)</option>
              {printers.filter((p) => p.active).map((p) => (
                <option key={p.id} value={p.id}>
                  → {p.name} ({p.printer_type === "a4" ? "Α4" : "80mm"})
                </option>
              ))}
            </select>
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={onPrintOrder}
              disabled={busy || cartEmpty || printers.filter((p) => p.active).length === 0}
              variant="outline"
              className="h-12 border-2 border-blue-400 text-blue-700 hover:bg-blue-50 font-bold text-sm"
              data-testid="print-order-btn"
            >
              <Printer size={16} className="mr-1.5" />
              Εκτύπωση Παραγγελίας
            </Button>
            <Button
              onClick={onSaveNonFiscal}
              disabled={busy || cartEmpty}
              variant="outline"
              className="h-12 border-2 border-slate-300 hover:bg-slate-100 font-bold text-sm"
              data-testid="save-non-fiscal-btn"
            >
              <Save size={16} className="mr-1.5" />
              Δισκέτα
            </Button>
          </div>
        </div>
      </div>

      {/* RIGHT: Numpad + Έκδοση Απόδειξης */}
      <div className="bg-slate-50 p-3 flex flex-col" data-testid="payment-numpad">
        <div className="grid grid-cols-3 gap-1.5">
          {keys.map((k, i) => (
            <button
              key={i}
              type="button"
              onClick={() => press(k)}
              data-testid={`pay-numpad-${k}`}
              className="h-24 rounded-md bg-white border-2 border-slate-200 text-2xl font-bold font-mono hover:bg-slate-100 active:bg-slate-200 active:scale-[0.96] transition-all"
            >
              {k === "back" ? <Delete size={20} className="mx-auto" /> : k}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => press("clear")}
          data-testid="pay-numpad-clear"
          className="mt-1.5 h-10 rounded-md bg-red-600 border-2 border-red-700 text-white text-base font-extrabold hover:bg-red-700 active:scale-[0.96] transition-all"
        >
          C (Καθαρισμός)
        </button>
        <div className="flex-1" />
        {/* Big "Έκδοση Απόδειξης" */}
        <Button
          onClick={onSubmit}
          disabled={busy || cartEmpty || cashInsufficient}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-14 font-bold text-lg shadow-md disabled:bg-slate-400 disabled:cursor-not-allowed"
          data-testid="confirm-payment-btn"
          title={cashInsufficient ? "Τα ληφθέντα μετρητά είναι μικρότερα από το σύνολο" : ""}
        >
          <Receipt size={22} className="mr-2" />
          {busy ? "Εκτύπωση..." : cashInsufficient ? "Ανεπαρκή Μετρητά" : "Έκδοση Απόδειξης"}
        </Button>
      </div>
    </div>
  );
}

export function ResultViewer({ log }) {
  return (
    <div className="space-y-3 max-h-[60vh] overflow-y-auto scroll-thin">
      <div className="grid grid-cols-2 gap-3 text-sm">
        <Stat label="Result code" value={log.result_code} ok={log.success} />
        <Stat label="Day ID" value={log.day_id || "—"} />
        <Stat label="Receipt #" value={log.receipt_counter || "—"} />
        <Stat label="Serial" value={log.serial_number || "—"} />
        <Stat label="Ημερομηνία" value={log.device_datetime || "—"} />
        <Stat label="Λεπτά για Z" value={log.minutes_to_z || "—"} />
        {log.invoice_nr && <Stat label="Invoice #" value={log.invoice_nr} />}
        {log.extra_line && <Stat label="Extra" value={log.extra_line} wide />}
      </div>
      <div>
        <Label className="text-xs">Command sent</Label>
        <pre className="bg-slate-900 text-slate-100 p-3 rounded-md text-xs overflow-x-auto font-mono whitespace-pre-wrap">
{log.command_text}
        </pre>
      </div>
      <div>
        <Label className="text-xs">Driver result</Label>
        <pre className="bg-slate-100 text-slate-800 p-3 rounded-md text-xs overflow-x-auto font-mono whitespace-pre-wrap">
{log.result_text}
        </pre>
      </div>
    </div>
  );
}

function Stat({ label, value, ok, wide }) {
  return (
    <div className={`rounded-md border border-slate-200 px-3 py-2 ${wide ? "col-span-2" : ""}`}>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div
        className={`font-mono text-sm ${
          ok === true
            ? "text-emerald-600 font-bold"
            : ok === false
            ? "text-red-600 font-bold"
            : "text-slate-900"
        }`}
      >
        {String(value)}
      </div>
    </div>
  );
}
